import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ThemeProvider, styled } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
  setDomain,
  setTopic,
  setBestPractice,
  setQuestion,
  setAuditLogs,
  setComments,
  setAssessmentQuestionStatus,
  setQuestionTags,
  setEvidence,
} from '../../../app/store/questionnaireAnswerSlice';
import { createComment, getAuditLogByQuestionAnswerId } from '../../../services/commentsLogs';
import { getManyEvidence } from '../../../services/questionAnswer';
import { getMultipleComments, updateAssessorAnswer, updateAssessorAnswerTags } from '../../../services/assessments';
import {
  addWorkflowQcComment,
  createWorkflowQcIssue,
  getQCReviews,
  getWorkflowQcComments,
  getWorkflowQcIssues,
  getWorkflowQcReviewCycle,
  submitWorkflowQcIssues,
  updateQcIssueStatus,
  updateQCReviewAdminResponse,
} from '../../../services/qcReview';
import { filterEvidenceForUser } from '../../../utils/questionEvidence';
import { AssessmentSurvey, Comment } from '../../../interfaces/assessment.interface';
import { Survey as SurveyType } from '../../../interfaces/survey.interface';
import { FileUpload } from '../../Forms/FileUpload';
import { REMEDIATION_VERIFICATION_STATUS } from '../../../services/surveyHelpers';
import {
  addQcComment,
  addQcIssue,
  replaceQcIssue,
  seedQcComments,
  seedQcCycle,
  seedQcIssues,
  seedQcNotes,
  seedWorkspaceFilters,
  setQcNoteResponse,
  setQcIssueStatus,
  setQcOnlyFilter,
  submitQcReview,
  dismissQcApproval,
  reopenQcApproval,
  toggleStatusFilter,
  clearStatusFilters,
} from '../../../app/store/assessmentWorkspaceSlice';
import { USER_TYPES } from '../../../services/user';
import { workflowVersion } from '../../../services/workflow';
import { isWorkflowFiveThreeOrGreater } from '../../../services/questionnaires';
import { QcFirstPassNote, QcIssue, QcIssueCategory, QcIssueStatus, QC_ISSUE_STATUS_LABELS } from '../../../interfaces/qcIssue.interface';
import { assessmentTheme } from '../../Themes/assessment';
import {
  deriveWorkspaceStage,
  getWorkspaceCapabilities,
  isAssessorRoundClosed,
  getOrderedVisibleQuestions,
  getFilteredEntries,
  tagsByQuestionFrom,
  resolveEvidenceFiles,
  getFindingsProgress,
  getQcApprovalProgress,
  getQcGuidance,
  getQcSummaryRows,
  hasActiveFilters,
  countQcIssuesByStatus,
  countUnaddressedFirstPass,
  groupQcCommentsByQuestion,
  groupQcIssuesByQuestion,
  ANSWER_FILTER_OPTIONS,
  toQcAuthor,
  OrderedQuestionEntry,
  QcSummaryRow,
} from './workspaceDerivations';
import { WorkspaceTraceContext } from './WorkspaceTraceContext';
import WorkspaceHeader from './WorkspaceHeader';
import QuestionHeaderRows from './QuestionHeaderRows';
import AssessorAnswerPanel from './AssessorAnswerPanel';
import QcReviewPanel from './QcReviewPanel';
import QuestionPagerFooter from './QuestionPagerFooter';
import NavigatorRail from './NavigatorRail';
import BulkUpdateModal from './BulkUpdateModal';
import SubmitQcIssuesModal from './SubmitQcIssuesModal';
import FirstPassBlockModal from './FirstPassBlockModal';
import QcApprovedNextStepsModal from './QcApprovedNextStepsModal';
import AssessorRoundModal from './AssessorRoundModal';
import MarkReadyPromptModal from './MarkReadyPromptModal';
import AdminRoundReturnedModal from './AdminRoundReturnedModal';
import { ACCENT_BLUE, SUCCESS_GREEN, WARN_ORANGE } from './qcTheme';

// Cancels part of the Layout's padding: title row 16px under the header bar, sticky pager flush with the scroller bottom.
const WorkspaceRoot = styled('div')({
  background: '#f3f5fb',
  color: 'rgba(0,0,0,0.87)',
  // index.css puts -apple-system first; the design is Roboto (the prototype's metrics differ visibly).
  fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
  marginTop: '-8px',
  marginBottom: '-24px',
});

// Children stretch (the rail opts out) so the card spans the row and the sticky pager pins to the real viewport bottom.
const ContentRow = styled('div')({
  display: 'flex',
  gap: '16px',
  marginTop: '16px',
});

const QuestionCard = styled('div')({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  background: '#fff',
  borderRadius: '4px',
  boxShadow: '0 2px 1px -1px rgba(0,0,0,0.2),0 1px 1px 0 rgba(0,0,0,0.14),0 1px 3px 0 rgba(0,0,0,0.12)',
});

// Unified workspace: answer and QC columns 50/50 with a hairline divider; QC wraps below full-width when narrow.
const WorkspaceColumns = styled('div')({
  display: 'flex',
  alignItems: 'stretch',
  flexWrap: 'wrap',
});

const AnswerColumn = styled('div')({
  flex: '1 1 480px',
  minWidth: 0,
  padding: '18px 24px 24px',
});

// Vertical separator only — the header's divider is the horizontal line; a top border here doubled it.
const QcColumn = styled('div')({
  flex: '1 1 480px',
  boxSizing: 'border-box',
  minWidth: 0,
  borderLeft: '1px solid rgba(0,0,0,0.12)',
  padding: '18px 20px 24px',
});

export interface AssessmentWorkspaceViewProps {
  // Opens the engine's v1 "TPN Assessment Complete" dialog — same modal and submit flow as the non-variant questionnaire.
  onSubmitAssessment?: () => void;
}

export default function AssessmentWorkspaceView({ onSubmitAssessment }: AssessmentWorkspaceViewProps): JSX.Element | null {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAppSelector(state => state.user);
  const { showSubmitAssessmentButton } = useAppSelector(state => state.secondaryLayoutButtonSlice);
  const { workflow } = useAppSelector(state => state.workflow);
  const { survey } = useAppSelector(state => state.surveys);
  const { assessment } = useAppSelector(state => state.assessments);
  const {
    questionnaire,
    question,
    questionStatus,
    bpStatus,
    assessmentQuestionsStatus,
    assessmentStatus,
    company,
    auditLogs,
    evidenceById,
    comments: allComments,
  } = useAppSelector(state => state.questionnaireAnswer);
  const {
    qcOnlyFilter,
    incompleteOnlyFilter,
    tagFilters,
    statusFilters,
    spAnswerFilters,
    assessorAnswerFilters,
    qcIssuesByQuestion,
    qcIssuesSeeded,
    qcNotesByQuestion,
    qcCommentsByQuestion,
    qcReviewSubmitted,
    qcRoundNumber,
    qcRoundClosed,
    qcApprovalDismissed,
    qcAssessmentApproved,
    sessionSaves,
  } = useAppSelector(state => state.assessmentWorkspace);
  const [traceText, setTraceText] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [checkedIds, setCheckedIds] = useState<number[]>([]);
  const [qcSubmitOpen, setQcSubmitOpen] = useState(false);
  const [qcBlockOpen, setQcBlockOpen] = useState(false);
  const [roundInfo, setRoundInfo] = useState<{ sent: number; approved: number } | null>(null);
  const [adminRoundInfo, setAdminRoundInfo] = useState<{ returned: number; approved: number } | null>(null);
  const [markReadyPromptOpen, setMarkReadyPromptOpen] = useState(false);
  // Gates the URL writer until incoming ?question= and filter deep links apply, so defaults can't clobber params.
  const [questionParamApplied, setQuestionParamApplied] = useState(false);
  const [filterParamsApplied, setFilterParamsApplied] = useState(false);
  const qcParamApplied = useRef(false);
  const requestedCommentIds = useRef<Set<number>>(new Set());
  const requestedEvidenceIds = useRef<Set<number>>(new Set());
  const defaultQcFilterApplied = useRef(false);
  const roundSeen = useRef(false);
  const auditLogsFetched = useRef<Record<number, boolean>>({});
  const qcIssuesFetchedFor = useRef<number | null>(null);

  const entries = useMemo(
    () => getOrderedVisibleQuestions(questionnaire, bpStatus, questionStatus),
    [questionnaire, bpStatus, questionStatus],
  );

  const tagsByQuestion = useMemo(
    () => tagsByQuestionFrom(assessmentQuestionsStatus),
    [assessmentQuestionsStatus],
  );

  // Click-driven like every other write here: patch the answer, then reflect it locally.
  const persistTags = async (questionId: number, tags: string[]) => {
    const answerId = assessmentQuestionsStatus?.[questionId]?.id;
    if (!answerId) return;
    try {
      await updateAssessorAnswerTags(answerId, tags);
      dispatch(setQuestionTags({ questionId, tags }));
    } catch (error) {
      console.error('Failed to update finding tags', error);
    }
  };

  const filters = useMemo(
    () => ({ statusFilters, tagFilters, spAnswerFilters, assessorAnswerFilters, qcOnlyFilter, incompleteOnlyFilter }),
    [statusFilters, tagFilters, spAnswerFilters, assessorAnswerFilters, qcOnlyFilter, incompleteOnlyFilter],
  );
  const filterContext = useMemo(
    () => ({ qcIssuesByQuestion, tagsByQuestion, assessmentQuestionsStatus, questionStatus }),
    [qcIssuesByQuestion, tagsByQuestion, assessmentQuestionsStatus, questionStatus],
  );
  const filteredEntries = useMemo(
    () => getFilteredEntries(entries, filters, filterContext),
    [entries, filters, filterContext],
  );

  const currentIndex = useMemo(
    () => entries.findIndex(e => e.question.id === question?.id),
    [entries, question?.id],
  );
  const currentEntry = currentIndex >= 0 ? entries[currentIndex] : undefined;
  const filteredIndex = useMemo(
    () => filteredEntries.findIndex(e => e.question.id === question?.id),
    [filteredEntries, question?.id],
  );

  const goToEntry = useCallback((entry: OrderedQuestionEntry) => {
    dispatch(setDomain(entry.domain));
    dispatch(setTopic(entry.topic));
    dispatch(setBestPractice(entry.bestPractice));
    dispatch(setQuestion(entry.question));
  }, [dispatch]);

  // Load persisted QC issues once per workflow; the API filters by role, so the client just fetches.
  useEffect(() => {
    const workflowId = workflow?.id;
    if (!workflowId || assessmentStatus !== 'Submitted' || qcIssuesFetchedFor.current === workflowId) return;
    qcIssuesFetchedFor.current = workflowId;
    getWorkflowQcIssues(workflowId)
      .then(issues => { dispatch(seedQcIssues(groupQcIssuesByQuestion(issues))); })
      .catch(error => { console.error('Failed to load QC issues', error); });
    // General QC threads — the API only serves TPN admins and the workflow's assessor.
    if (user?.type === USER_TYPES.TPN_ADMIN || user?.type === USER_TYPES.ASSESSOR) {
      getWorkflowQcComments(workflowId)
        .then(comments => { dispatch(seedQcComments(groupQcCommentsByQuestion(comments))); })
        .catch(error => { console.error('Failed to load QC comments', error); });
      // Cycle state makes qcReviewSubmitted survive reloads.
      getWorkflowQcReviewCycle(workflowId)
        .then(cycle => { dispatch(seedQcCycle(cycle)); })
        .catch(error => { console.error('Failed to load QC review cycle', error); });
    }
    // First Pass rollup notes ("No issue raised" / escalations) — admin-only artifacts.
    if (user?.type === USER_TYPES.TPN_ADMIN) {
      getQCReviews(workflowId)
        .then(reviews => {
          const notes: Record<number, QcFirstPassNote> = {};
          reviews.forEach(review => {
            if (review.targetGrain !== 'rollup' || !review.questionId) return;
            if (review.verdict !== 'agree' && review.verdict !== 'not_evaluated') return;
            notes[review.questionId] = {
              reviewId: review.id,
              verdict: review.verdict,
              rationale: review.rationale || '',
              confidence: review.confidence ?? undefined,
              suspectedInjection: !!review.suspectedInjection,
              adminResponse: review.adminResponse ?? undefined,
              adminResponseNote: review.adminResponseNote || '',
              spellingNotes: review.spellingNotes || [],
              crossReferences: review.crossReferences || [],
            };
          });
          dispatch(seedQcNotes(notes));
        })
        .catch(error => { console.error('Failed to load First Pass notes', error); });
    }
  }, [workflow?.id, assessmentStatus, user?.type, dispatch]);

  // ?filter=qc deep link: apply the QC filter on mount and land on the first flagged question.
  useEffect(() => {
    if (qcParamApplied.current || !entries.length) return;
    if (searchParams.get('filter') !== 'qc') {
      qcParamApplied.current = true;
      return;
    }
    // Landing on the first flagged question needs the issues fetch to have settled.
    if (!qcIssuesSeeded) return;
    qcParamApplied.current = true;
    dispatch(setQcOnlyFilter(true));
    const first = entries.find(e => (qcIssuesByQuestion[e.question.id] || []).length > 0);
    if (first) goToEntry(first);
  }, [entries, searchParams, qcIssuesByQuestion, qcIssuesSeeded, goToEntry, dispatch]);

  // Filter deep links, one repeated param per family; unknown values drop, absent params still replace the selection — the link is authoritative.
  useEffect(() => {
    if (filterParamsApplied) return;
    setFilterParamsApplied(true);
    const knownStatuses = Object.keys(QC_ISSUE_STATUS_LABELS);
    const statuses = searchParams.getAll('status').filter(v => knownStatuses.includes(v)) as QcIssueStatus[];
    const tags = searchParams.getAll('tag');
    const sp = searchParams.getAll('sp').filter(v => ANSWER_FILTER_OPTIONS.includes(v));
    const assessor = searchParams.getAll('assessor').filter(v => ANSWER_FILTER_OPTIONS.includes(v));
    const qcOnly = searchParams.get('qc') === '1';
    const incomplete = searchParams.get('incomplete') === '1';
    if (!statuses.length && !tags.length && !sp.length && !assessor.length && !qcOnly && !incomplete) return;
    dispatch(seedWorkspaceFilters({
      statusFilters: statuses,
      tagFilters: tags,
      spAnswerFilters: sp,
      assessorAnswerFilters: assessor,
      qcOnlyFilter: qcOnly,
      incompleteOnlyFilter: incomplete,
    }));
  }, [searchParams, filterParamsApplied, dispatch]);

  // ?question=<id> deep link (v1 contract); declared after the filter effect so an explicit question wins the landing.
  useEffect(() => {
    if (questionParamApplied || !entries.length) return;
    setQuestionParamApplied(true);
    const questionParam = Number(searchParams.get('question'));
    if (!questionParam) return;
    const target = entries.find(e => e.question.id === questionParam);
    if (target && target.question.id !== question?.id) goToEntry(target);
  }, [entries, searchParams, questionParamApplied, question?.id, goToEntry]);

  // URL out via raw history.replaceState, NOT setSearchParams — router navigation re-runs the page loaders per click; preserving history.state keeps react-router's key intact.
  useEffect(() => {
    if (!questionParamApplied || !filterParamsApplied) return;
    const url = new URL(window.location.href);
    const before = url.searchParams.toString();
    if (question?.id) url.searchParams.set('question', String(question.id));
    const writeList = (key: string, values: string[]) => {
      url.searchParams.delete(key);
      values.forEach(value => url.searchParams.append(key, value));
    };
    writeList('status', statusFilters);
    writeList('tag', tagFilters);
    writeList('sp', spAnswerFilters);
    writeList('assessor', assessorAnswerFilters);
    if (qcOnlyFilter) url.searchParams.set('qc', '1'); else url.searchParams.delete('qc');
    if (incompleteOnlyFilter) url.searchParams.set('incomplete', '1'); else url.searchParams.delete('incomplete');
    // Legacy ?filter=qc is normalized to qc=1 once its state has been seeded.
    url.searchParams.delete('filter');
    if (url.searchParams.toString() === before) return;
    window.history.replaceState(window.history.state, '', url);
  }, [
    questionParamApplied, filterParamsApplied, question?.id, statusFilters, tagFilters,
    spAnswerFilters, assessorAnswerFilters, qcOnlyFilter, incompleteOnlyFilter,
  ]);

  // Audit-log dictionary fills per question answer (v1 behavior); without it the modal only shows logs another screen loaded.
  const currentQuestionAnswerId = question?.id
    ? questionStatus[question.id]?.questionAnswer?.id
    : undefined;
  useEffect(() => {
    if (!currentQuestionAnswerId || auditLogsFetched.current[currentQuestionAnswerId]) return;
    auditLogsFetched.current[currentQuestionAnswerId] = true;
    getAuditLogByQuestionAnswerId(currentQuestionAnswerId)
      .then(logs => dispatch(setAuditLogs(logs)))
      .catch(() => { delete auditLogsFetched.current[currentQuestionAnswerId]; });
  }, [currentQuestionAnswerId, dispatch]);

  const currentQuestionAnswer = question?.id ? questionStatus[question.id]?.questionAnswer : undefined;
  const currentAssessorAnswer = question?.id ? assessmentQuestionsStatus?.[question.id] : undefined;

  // v1's evidence hydration: fetch the attachment ids the shared cache lacks (read-only; failures retry on revisit).
  useEffect(() => {
    const missing = (currentQuestionAnswer?.attachments || [])
      .filter(id => !evidenceById[id] && !requestedEvidenceIds.current.has(id));
    if (!missing.length) return;
    missing.forEach(id => requestedEvidenceIds.current.add(id));
    getManyEvidence(missing)
      .then(evidence => dispatch(setEvidence(evidence)))
      .catch(error => {
        missing.forEach(id => requestedEvidenceIds.current.delete(id));
        console.error('Failed to load evidence', error);
      });
  }, [currentQuestionAnswer?.attachments, evidenceById, dispatch]);

  // Same records the v1 questionnaire's Attachments modal shows, filtered by role.
  const evidenceFiles = useMemo<FileUpload[]>(() => filterEvidenceForUser(
    resolveEvidenceFiles(currentQuestionAnswer?.attachments, evidenceById),
    user, survey as SurveyType | undefined, assessment,
  ), [currentQuestionAnswer?.attachments, evidenceById, user, survey, assessment]);

  const questionComments = useMemo<Comment[]>(() => {
    const raw = currentAssessorAnswer?.comments || [];
    if (!allComments) return [];
    return raw
      .map(c => (typeof c === 'number' ? allComments[c] : allComments[c.id as number] || c))
      .filter((c): c is Comment => !!c && typeof c.user === 'object');
  }, [currentAssessorAnswer?.comments, allComments]);

  // Comments renders comment.user as a full object, so any id we don't hold must be fetched.
  useEffect(() => {
    const raw = currentAssessorAnswer?.comments || [];
    const missing = raw
      .map(c => (typeof c === 'number' ? c : c.id))
      .filter((id): id is number => !!id && !allComments?.[id] && !requestedCommentIds.current.has(id));
    if (!missing.length) return;
    missing.forEach(id => requestedCommentIds.current.add(id));
    getMultipleComments(missing)
      .then(fetched => dispatch(setComments(fetched)))
      .catch(error => { console.error('Failed to load comments', error); });
  }, [currentAssessorAnswer?.comments, allComments, dispatch]);

  const assessmentSurvey = useMemo<AssessmentSurvey | undefined>(() => {
    if (!assessment || !survey) return undefined;
    return (assessment.surveys as AssessmentSurvey[]).find(assSur =>
      (typeof assSur.survey === 'number' ? assSur.survey === survey.id : assSur.survey.id === survey.id));
  }, [assessment, survey]);

  const commentsDisabled = user?.type !== USER_TYPES.TPN_ADMIN
    && currentAssessorAnswer?.verificationStatus === REMEDIATION_VERIFICATION_STATUS.reviewed;

  // Comment posting attaches to the assessor answer (engine's path); the answer must already exist.
  const handlePostComment = useCallback(async (text: string, attachments: number[]) => {
    if (!user || !currentAssessorAnswer?.id) return;
    const comment = await createComment({ user, text, attachments } as unknown as Comment);
    const existingIds = (currentAssessorAnswer.comments || [])
      .map(c => (typeof c === 'number' ? c : c.id!));
    await updateAssessorAnswer({
      ...currentAssessorAnswer,
      comments: [...existingIds, comment.id!],
    } as never);
    // Appends to the question's comments and seeds the dictionary in one go, so list and count update without reload.
    if (question) {
      dispatch(setAssessmentQuestionStatus({
        question,
        status: currentAssessorAnswer.status || 'incomplete',
        comment,
      }));
    }
  }, [user, currentAssessorAnswer, question, dispatch]);

  const allIssues = useMemo(
    () => Object.values(qcIssuesByQuestion).reduce<QcIssue[]>((acc, list) => acc.concat(list), []),
    [qcIssuesByQuestion],
  );

  const stage = deriveWorkspaceStage(assessmentStatus, qcReviewSubmitted);
  // Round closed = with TPN: the server stamp is truth (it survives partial returns); the status derivation covers the instant before the cycle refetch.
  const roundClosed = qcReviewSubmitted && (qcRoundClosed || isAssessorRoundClosed(allIssues));
  const qcProgress = getQcApprovalProgress(allIssues);
  const qcComplete = qcProgress.total > 0 && qcProgress.approved === qcProgress.total;
  const qcGuidance = useMemo(() => getQcGuidance({
    userType: user?.type,
    stage,
    qcReviewSubmitted,
    qcAssessmentApproved,
    roundNumber: qcRoundNumber,
    qcIssues: allIssues,
    roundClosed,
  }), [user?.type, stage, qcReviewSubmitted, qcAssessmentApproved, qcRoundNumber, allIssues, roundClosed]);

  const capabilities = getWorkspaceCapabilities(
    user?.type, stage, qcReviewSubmitted, roundClosed, qcAssessmentApproved,
    qcReviewSubmitted && qcComplete,
  );
  const isAssessor = user?.type === USER_TYPES.ASSESSOR;
  const isAdmin = user?.type === USER_TYPES.TPN_ADMIN;

  // Seed the assessor's default QC filters once — only when no deep link or active filter claimed the view; clearing sticks via the ref.
  useEffect(() => {
    if (defaultQcFilterApplied.current || !filterParamsApplied) return;
    if (!isAssessor || !qcReviewSubmitted) return;
    if (searchParams.get('filter') === 'qc' || searchParams.get('question')) return;
    if (hasActiveFilters(filters)) return;
    if (!allIssues.some(issue => ['pending_assessor_action', 'needs_update'].includes(issue.status))) return;
    defaultQcFilterApplied.current = true;
    dispatch(seedWorkspaceFilters({ ...filters, statusFilters: ['pending_assessor_action', 'needs_update'] }));
  }, [filterParamsApplied, isAssessor, qcReviewSubmitted, searchParams, filters, allIssues, dispatch]);
  const isFiveThreeOrGreater = workflow?.workflowType ? isWorkflowFiveThreeOrGreater(workflow.workflowType) : false;
  const userName = user ? [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || '' : '';
  const currentAuthor = useMemo(() => toQcAuthor(userName, user?.type), [userName, user?.type]);

  const unitLabel = useMemo(() => {
    if (!company || !survey) return undefined;
    if (survey.site) return company.locations?.find(loc => loc.id === survey.site)?.name;
    if (survey.application) {
      const app = company.companyApplications?.find(ca => ca.application.id === survey.application);
      return app ? `${app.application.name}${survey.version ? ` ${survey.version}` : ''}` : undefined;
    }
    return undefined;
  }, [company, survey]);

  // Both relations are typed `number | FullObject`.
  const assessorCompanyName = useMemo(() => {
    const assessor = assessment?.assessor;
    return assessor && typeof assessor !== 'number' ? assessor.name : undefined;
  }, [assessment?.assessor]);

  const assessorName = useMemo(() => {
    const assigned = assessment?.assignedAssessor;
    if (!assigned || typeof assigned === 'number') return undefined;
    return [assigned.firstName, assigned.lastName].filter(Boolean).join(' ') || assigned.email || undefined;
  }, [assessment?.assignedAssessor]);

  const findingsProgress = getFindingsProgress(entries, assessmentQuestionsStatus);
  const allApproved = isAdmin && qcReviewSubmitted && qcComplete;
  const qcSummaryRows = useMemo(
    () => getQcSummaryRows(entries, qcIssuesByQuestion),
    [entries, qcIssuesByQuestion],
  );
  // The gauge tracks whichever loop is live: findings while assessing, QC from submission (not only once issues ship).
  const ring = stage !== 'in_progress'
    ? {
      title: qcComplete ? 'QC Approved' : 'QC In Progress',
      done: qcProgress.approved,
      total: qcProgress.total,
      color: qcComplete ? SUCCESS_GREEN : WARN_ORANGE,
      trackColor: qcComplete ? 'rgba(46,125,50,0.14)' : 'rgba(230,81,0,0.14)',
    }
    : (() => {
      // Completion gets its payoff: the gauge flips green when every finding is saved.
      const findingsDone = findingsProgress.total > 0 && findingsProgress.saved === findingsProgress.total;
      return {
        title: findingsDone ? 'Findings Complete' : 'Findings In-Progress',
        done: findingsProgress.saved,
        total: findingsProgress.total,
        color: findingsDone ? SUCCESS_GREEN : ACCENT_BLUE,
        trackColor: findingsDone ? 'rgba(46,125,50,0.14)' : 'rgba(25,118,210,0.14)',
      };
    })();

  const filterNames = useMemo(() => {
    const names = [
      ...tagFilters,
      ...spAnswerFilters.map(label => `SP: ${label}`),
      ...assessorAnswerFilters.map(label => `Assessor: ${label}`),
      ...statusFilters.map(status => QC_ISSUE_STATUS_LABELS[status]),
    ];
    if (qcOnlyFilter) names.push('Has QC Issues');
    if (incompleteOnlyFilter) names.push('Incomplete');
    return names;
  }, [tagFilters, spAnswerFilters, assessorAnswerFilters, statusFilters, qcOnlyFilter, incompleteOnlyFilter]);

  const handleToggleStatusFilter = useCallback((status: QcIssueStatus) => {
    dispatch(toggleStatusFilter(status));
  }, [dispatch]);

  const handleNoteResponse = useCallback(async (adminResponse: 'accept' | 'reject', noteText?: string) => {
    if (!question?.id) return;
    const noteForQuestion = qcNotesByQuestion[question.id];
    if (!noteForQuestion) return;
    try {
      await updateQCReviewAdminResponse(noteForQuestion.reviewId, adminResponse, noteText);
      dispatch(setQcNoteResponse({ questionId: question.id, adminResponse, note: noteText }));
    } catch (error) {
      console.error('Failed to record First Pass response', error);
    }
  }, [question?.id, qcNotesByQuestion, dispatch]);

  // Manual issue add: optimistic append under a temp id, then swap in the persisted issue so later actions carry the real id.
  const handleAddQcIssue = useCallback(async (category: QcIssueCategory, text: string) => {
    const workflowId = workflow?.id;
    const questionId = question?.id;
    if (!questionId || !workflowId) return;
    const tempId = -Date.now();
    dispatch(addQcIssue({ questionId, category, text, author: currentAuthor, id: tempId }));
    try {
      const created = await createWorkflowQcIssue(workflowId, questionId, category, text);
      dispatch(replaceQcIssue({ questionId, issueId: tempId, issue: created }));
    } catch (error) {
      console.error('Failed to add QC issue', error);
    }
  }, [question?.id, workflow?.id, currentAuthor, dispatch]);

  // General QC thread post: optimistic slice append, then persist to QCComment.
  const handlePostQcComment = useCallback(async (text: string) => {
    const workflowId = workflow?.id;
    if (!question?.id || !workflowId) return;
    dispatch(addQcComment({ questionId: question.id, text, author: currentAuthor }));
    try {
      await addWorkflowQcComment(workflowId, question.id, text);
    } catch (error) {
      console.error('Failed to post QC comment', error);
    }
  }, [question?.id, workflow?.id, currentAuthor, dispatch]);

  const handleSetIssueStatus = useCallback(async (issueId: number, status: QcIssueStatus) => {
    if (!currentEntry || !user) return;
    dispatch(setQcIssueStatus({
      questionId: currentEntry.question.id,
      issueId,
      status,
      by: currentAuthor.name,
    }));
    // Optimistic by design: the closing PATCH triggers the server recheck, so waiting would show nothing; checked against the post-dispatch shape, shown once.
    if (status === 'ready_for_tpn_review' && isAssessor && !roundSeen.current) {
      const counted = allIssues.filter(issue => issue.status !== 'first_pass');
      const stillOpen = counted.some(issue => issue.id !== issueId
        && ['pending_assessor_action', 'needs_update'].includes(issue.status));
      if (counted.length && !stillOpen) {
        roundSeen.current = true;
        setRoundInfo({
          sent: counted.filter(issue => issue.id === issueId || issue.status === 'ready_for_tpn_review').length,
          approved: counted.filter(issue => issue.status === 'approved').length,
        });
      }
    }
    // Persist after the optimistic move; the cycle refetch keeps qcRoundNumber live (returns increment the round server-side).
    const workflowId = workflow?.id;
    if (issueId > 0 && workflowId) {
      try {
        await updateQcIssueStatus(issueId, status);
        const cycle = await getWorkflowQcReviewCycle(workflowId);
        dispatch(seedQcCycle(cycle));
      } catch (error) {
        console.error('Failed to update QC issue status', error);
        return;
      }
    }
    // Pessimistic: the PATCH sends the assessor email, so show only once it succeeds; with anything returned, mirror the assessor's round modal.
    if (isAdmin && ['approved', 'needs_update'].includes(status)) {
      const counted = allIssues.filter(issue => issue.status !== 'first_pass');
      const stillReady = counted.some(issue => issue.id !== issueId && issue.status === 'ready_for_tpn_review');
      const returned = counted.filter(issue => issue.id !== issueId && issue.status === 'needs_update').length
        + (status === 'needs_update' ? 1 : 0);
      if (!stillReady && returned > 0) {
        setAdminRoundInfo({
          returned,
          approved: counted.filter(issue => (issue.id === issueId ? status === 'approved' : issue.status === 'approved')).length,
        });
      }
    }
  }, [dispatch, currentEntry, user, userName, isAssessor, isAdmin, allIssues, workflow?.id]);

  // Post-save nudge; persists run sequentially (the last open PATCH triggers the server recheck — concurrent PATCHes race it), round-close checked once post-batch.
  const openIssuesForQuestion = useMemo(() => (question?.id
    ? (qcIssuesByQuestion[question.id] || []).filter(
      issue => ['pending_assessor_action', 'needs_update'].includes(issue.status),
    )
    : []), [qcIssuesByQuestion, question?.id]);

  const handleFindingSaved = useCallback(() => {
    if (!isAssessor || !qcReviewSubmitted || !capabilities.markReadyForTpnReview) return;
    if (!openIssuesForQuestion.length) return;
    setMarkReadyPromptOpen(true);
  }, [isAssessor, qcReviewSubmitted, capabilities.markReadyForTpnReview, openIssuesForQuestion.length]);

  const handleMarkOpenIssuesReady = useCallback(async () => {
    setMarkReadyPromptOpen(false);
    if (!currentEntry || !user || !openIssuesForQuestion.length) return;
    const readyIds = new Set(openIssuesForQuestion.map(issue => issue.id));
    openIssuesForQuestion.forEach(issue => {
      dispatch(setQcIssueStatus({
        questionId: currentEntry.question.id,
        issueId: issue.id,
        status: 'ready_for_tpn_review',
        by: currentAuthor.name,
      }));
    });
    // Optimistic round modal here too (the last PATCH dispatches the recheck).
    if (!roundSeen.current) {
      const counted = allIssues.filter(issue => issue.status !== 'first_pass');
      const stillOpen = counted.some(issue => !readyIds.has(issue.id)
        && ['pending_assessor_action', 'needs_update'].includes(issue.status));
      if (counted.length && !stillOpen) {
        roundSeen.current = true;
        setRoundInfo({
          sent: counted.filter(issue => readyIds.has(issue.id) || issue.status === 'ready_for_tpn_review').length,
          approved: counted.filter(issue => issue.status === 'approved').length,
        });
      }
    }
    const workflowId = workflow?.id;
    if (workflowId) {
      try {
        for (const issue of openIssuesForQuestion.filter(i => i.id > 0)) {
          // eslint-disable-next-line no-await-in-loop
          await updateQcIssueStatus(issue.id, 'ready_for_tpn_review');
        }
        const cycle = await getWorkflowQcReviewCycle(workflowId);
        dispatch(seedQcCycle(cycle));
      } catch (error) {
        console.error('Failed to update QC issue status', error);
      }
    }
  }, [dispatch, currentEntry, user, openIssuesForQuestion, allIssues, workflow?.id, currentAuthor.name]);

  // #CODE/modal-row navigation clears filters so the target question is never hidden (prototype linkParts onGo).
  const handleNavigateToCode = useCallback((shortCode: string) => {
    const entry = entries.find(e => e.question.shortCode === shortCode);
    if (!entry) return;
    dispatch(clearStatusFilters());
    goToEntry(entry);
  }, [entries, goToEntry, dispatch]);

  const unaddressedFirstPass = countUnaddressedFirstPass(qcIssuesByQuestion);

  // Submit QC Issues is blocked while any First Pass draft is unaddressed.
  const handleOpenQcSubmit = useCallback(() => {
    if (countUnaddressedFirstPass(qcIssuesByQuestion) > 0) setQcBlockOpen(true);
    else setQcSubmitOpen(true);
  }, [qcIssuesByQuestion]);

  const handleReviewFirstPass = useCallback(() => {
    setQcBlockOpen(false);
    dispatch(clearStatusFilters());
    dispatch(toggleStatusFilter('first_pass'));
    const first = entries.find(e => (qcIssuesByQuestion[e.question.id] || []).some(i => i.status === 'first_pass'));
    if (first) goToEntry(first);
  }, [entries, qcIssuesByQuestion, goToEntry, dispatch]);

  const handleSummaryRowClick = useCallback((row: QcSummaryRow) => {
    setQcSubmitOpen(false);
    dispatch(clearStatusFilters());
    goToEntry(row.entry);
  }, [goToEntry, dispatch]);

  if (!questionnaire || !currentEntry) return null;

  const currentQuestion = currentEntry.question;
  const currentIssues = qcIssuesByQuestion[currentQuestion.id] || [];
  const currentTags = tagsByQuestion[currentQuestion.id] || [];

  return (
    <ThemeProvider theme={assessmentTheme}>
      <WorkspaceTraceContext.Provider value={{ traceText, setTraceText }}>
        <WorkspaceRoot>
          <WorkspaceHeader
            company={company}
            unitLabel={unitLabel}
            dateAccepted={assessment?.dateAccepted}
            qcCounts={countQcIssuesByStatus(allIssues)}
            statusFilters={statusFilters}
            showQcStats={(isAdmin || qcReviewSubmitted) && capabilities.qcIssuesVisible}
            showFirstPassTile={capabilities.firstPassVisible}
            canSubmitQcIssues={capabilities.submitQcIssues}
            canSubmitAssessment={isAssessor && showSubmitAssessmentButton && !!onSubmitAssessment}
            showApproveButton={allApproved && !qcAssessmentApproved}
            assessmentApproved={qcAssessmentApproved}
            ring={ring}
            guidance={qcGuidance}
            isAssessor={isAssessor}
            assessorCompanyName={assessorCompanyName}
            assessorName={assessorName}
            onToggleStatusFilter={handleToggleStatusFilter}
            onSubmitQcIssues={handleOpenQcSubmit}
            onSubmitAssessment={onSubmitAssessment}
            onApproveAssessment={() => dispatch(reopenQcApproval())}
          />
          <ContentRow>
          <QuestionCard>
            <QuestionHeaderRows
              domainTitle={currentEntry.domain.code
                ? `${currentEntry.domain.code}. ${currentEntry.domain.title}`
                : currentEntry.domain.title}
              topicTitle={currentEntry.topic.title}
              bpLabel={currentQuestion.shortCode || currentEntry.bestPractice.title}
              questionTitle={currentQuestion.title}
              isBestPractice={currentQuestion.isBestPractice}
              filterNames={filterNames}
              currentInFilteredSet={filteredIndex !== -1 || filteredEntries.length === 0}
              onJumpToFirst={() => { if (filteredEntries.length) goToEntry(filteredEntries[0]); }}
              onClearFilters={() => dispatch(clearStatusFilters())}
            />
            <WorkspaceColumns>
              <AnswerColumn>
                <AssessorAnswerPanel
                  question={currentQuestion}
                  questionAnswer={questionStatus[currentQuestion.id]?.questionAnswer}
                  auditLogs={auditLogs}
                  evidenceFiles={evidenceFiles}
                  comments={questionComments}
                  assessmentSurvey={assessmentSurvey}
                  commentsDisabled={commentsDisabled}
                  onPostComment={handlePostComment}
                  existing={assessmentQuestionsStatus?.[currentQuestion.id]}
                  editable={capabilities.assessorAnswerEditable}
                  isFiveThreeOrGreater={isFiveThreeOrGreater}
                  tags={currentTags}
                  entries={entries}
                  sessionSave={sessionSaves[currentQuestion.id]}
                  onAddTag={tag => persistTags(currentQuestion.id, [...currentTags, tag])}
                  onRemoveTag={tag => persistTags(currentQuestion.id, currentTags.filter(t => t !== tag))}
                  onNavigateToCode={handleNavigateToCode}
                  onFindingSaved={handleFindingSaved}
                />
              </AnswerColumn>
              <QcColumn>
                <QcReviewPanel
                  issues={currentIssues}
                  comments={qcCommentsByQuestion[currentQuestion.id] || []}
                  entries={entries}
                  questionId={currentQuestion.id}
                  questionCode={currentQuestion.shortCode || currentEntry.bestPractice.title}
                  capabilities={capabilities}
                  qcStageVisible={capabilities.qcIssuesVisible}
                  qcReviewSubmitted={qcReviewSubmitted}
                  currentAuthor={currentAuthor}
                  currentRound={qcRoundNumber}
                  note={qcNotesByQuestion[currentQuestion.id]}
                  onSetStatus={handleSetIssueStatus}
                  onNavigateToCode={handleNavigateToCode}
                  onNoteResponse={handleNoteResponse}
                  onPostQcComment={handlePostQcComment}
                  onAddQcIssue={handleAddQcIssue}
                />
              </QcColumn>
            </WorkspaceColumns>
            <QuestionPagerFooter
              hasPrevious={filteredIndex > 0 || (filteredIndex === -1 && filteredEntries.some(e => entries.indexOf(e) < currentIndex))}
              hasNext={filteredIndex !== -1 ? filteredIndex < filteredEntries.length - 1 : filteredEntries.some(e => entries.indexOf(e) > currentIndex)}
              onPrevious={() => {
                const candidates = filteredEntries.filter(e => entries.indexOf(e) < currentIndex);
                if (candidates.length) goToEntry(candidates[candidates.length - 1]);
              }}
              onNext={() => {
                const candidate = filteredEntries.find(e => entries.indexOf(e) > currentIndex);
                if (candidate) goToEntry(candidate);
              }}
            />
          </QuestionCard>
          <NavigatorRail
            entries={entries}
            currentQuestionId={currentQuestion.id}
            versionLabel={workflowVersion(workflow?.workflowType)}
            capabilities={capabilities}
            checkedIds={checkedIds}
            onToggleCheck={questionId => setCheckedIds(prev =>
              (prev.includes(questionId) ? prev.filter(id => id !== questionId) : [...prev, questionId]))}
            onSetChecked={setCheckedIds}
            onNavigate={goToEntry}
            onOpenBulk={() => setBulkOpen(true)}
          />
          </ContentRow>
          <BulkUpdateModal
            open={bulkOpen}
            entries={entries.filter(e => checkedIds.includes(e.question.id))}
            onApplied={() => { setBulkOpen(false); setCheckedIds([]); }}
            onClose={() => setBulkOpen(false)}
          />
          <SubmitQcIssuesModal
            open={qcSubmitOpen}
            rows={qcSummaryRows}
            onGoToQuestion={handleSummaryRowClick}
            onConfirm={async () => {
              // Pessimistic — the endpoint publishes and emails the assessor; on failure the dialog stays open to retry.
              if (!workflow?.id) return;
              try {
                await submitWorkflowQcIssues(workflow.id);
                dispatch(submitQcReview());
                setQcSubmitOpen(false);
              } catch (error) {
                console.error('Failed to submit QC issues', error);
              }
            }}
            onClose={() => setQcSubmitOpen(false)}
          />
          <FirstPassBlockModal
            open={qcBlockOpen}
            openCount={unaddressedFirstPass}
            onReviewFirstPass={handleReviewFirstPass}
            onClose={() => setQcBlockOpen(false)}
          />
          <QcApprovedNextStepsModal
            open={allApproved && !qcApprovalDismissed && !qcAssessmentApproved}
            approvedCount={qcSummaryRows.length}
            unitLabel={unitLabel}
            onGoToAssessments={() => navigate('/admin/assessments')}
            onClose={() => dispatch(dismissQcApproval())}
          />
          <AssessorRoundModal
            open={roundInfo !== null}
            sentCount={roundInfo?.sent || 0}
            roundNumber={qcRoundNumber}
            approvedCount={roundInfo?.approved || 0}
            onClose={() => setRoundInfo(null)}
          />
          <AdminRoundReturnedModal
            open={adminRoundInfo !== null}
            returnedCount={adminRoundInfo?.returned || 0}
            approvedCount={adminRoundInfo?.approved || 0}
            roundNumber={qcRoundNumber}
            onClose={() => setAdminRoundInfo(null)}
          />
          <MarkReadyPromptModal
            open={markReadyPromptOpen}
            openCount={openIssuesForQuestion.length}
            onMarkReady={handleMarkOpenIssuesReady}
            onClose={() => setMarkReadyPromptOpen(false)}
          />
        </WorkspaceRoot>
      </WorkspaceTraceContext.Provider>
    </ThemeProvider>
  );
}
